/**
 * CustomAuthenticator Component
 * Professional marketing-focused authentication experience
 * Matches the casino gaming theme with red/gold colors
 */

import React from 'react';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import type { AuthUser } from 'aws-amplify/auth';
import './CustomAuthenticator.css';

interface CustomAuthenticatorProps {
  children: ({ signOut, user }: { 
    signOut?: (data?: any) => void; 
    user?: AuthUser; 
  }) => React.ReactElement;
}

const MarketingWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="custom-authenticator-container">
      <div className="auth-background">
        <div className="auth-overlay"></div>
      </div>
      
      <div className="auth-layout">
        {/* Left Column - Login Form and Marketing */}
        <div className="auth-left-column">
          {/* Login Form */}
          <div className="auth-form-card">
            {children}
            <div className='d-flex mt-5 justify-content-center'>
              <a 
                  href="https://aws.amazon.com/contact-us/sales-support/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="cta-link btn settings-button fs-5"
                >
                  Book a demo walk-through today
                </a>
            </div>
          </div>
        </div>

        {/* Right Column - Hero Card and Video */}
        <div className="auth-right-column">
          {/* Hero Card */}
          <div className="auth-hero-card">
            <div className="auth-marketing-card">
              <h3 className="marketing-headline">
                Experience the Future of Interactive Gaming
              </h3>
              <p className="marketing-description">
                Stream interactive casino experiences powered by Amazon GameLift Streams and Amazon IVS Real-time. 
                Cloud-rendered games delivered in near real-time latency to any device. Add messaging with AWS AppSync 
                and create interactive gaming communities where players and viewers are all part of the action.
              </p>
            </div>
            {/* <div className="auth-header">
              <h1 className="auth-title">Casino Gaming Streams</h1>
              <p className="auth-subtitle">Interactive Gaming Experience</p>
            </div> */}
            {/* <div className="hero-icon-container">
              <i className="bi bi-dice-6-fill hero-icon"></i>
              <div className="hero-text">
                <span>Interactive Casino Gaming</span>
                <small>Powered by AWS</small>
              </div>
            </div> */}
          </div>

          {/* Video Player */}
          <div className="auth-video-card p-4">
            <video 
              autoPlay 
              loop 
              muted 
              playsInline
              className="auth-video"
            >
              <source src="/amazon-ivs-gamelift-slots.mp4" type="video/mp4" />
            </video>
          </div>
          <hr />
          <div className="aws-branding">
            <div className="d-flex justify-content-center">
              <p className="powered-by">Powered by AWS</p>
            </div>
            <div className="service-badges">
              <a href="https://aws.amazon.com/gamelift/streams/" target="_blank" rel="noopener noreferrer" className="service-badge">GameLift</a>
              <a href="https://aws.amazon.com/ivs/features/real-time-streaming/" target="_blank" rel="noopener noreferrer" className="service-badge">IVS Real-time</a>
              <a href="https://aws.amazon.com/appsync/" target="_blank" rel="noopener noreferrer" className="service-badge">AppSync</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CustomAuthenticator: React.FC<CustomAuthenticatorProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  const theme = {
    name: 'casino-theme',
    tokens: {
      colors: {
        background: {
          primary: { value: 'transparent' },
          secondary: { value: 'rgba(0, 0, 0, 0.1)' }
        },
        border: {
          primary: { value: '#d4af37' },
          focus: { value: '#ffd700' }
        },
        brand: {
          primary: {
            '10': { value: '#d4af37' },
            '20': { value: '#d4af37' },
            '40': { value: '#d4af37' },
            '60': { value: '#d4af37' },
            '80': { value: '#d4af37' },
            '90': { value: '#ffd700' },
            '100': { value: '#ffd700' }
          }
        },
        font: {
          primary: { value: '#ffffff' },
          secondary: { value: '#d4af37' },
          interactive: { value: '#000000' }
        }
      }
    }
  };

  const handleAuthStateChange = React.useCallback((props: { signOut?: (data?: any) => void; user?: AuthUser }) => {
    if (props.user && !isAuthenticated) {
      // Use setTimeout to defer state update to next tick
      setTimeout(() => {
        setIsAuthenticated(true);
      }, 0);
    }
  }, [isAuthenticated]);

  return (
    <ThemeProvider theme={theme}>
      {!isAuthenticated ? (
        <MarketingWrapper>
          <Authenticator
            hideSignUp={true}
            loginMechanisms={['email']}
            className="casino-auth"
          >
            {(props) => {
              handleAuthStateChange(props);
              return <></>;
            }}
          </Authenticator>
        </MarketingWrapper>
      ) : (
        <Authenticator
          hideSignUp={true}
          loginMechanisms={['email']}
          className="casino-auth"
        >
          {children}
        </Authenticator>
      )}
    </ThemeProvider>
  );
};